import React from 'react';
import classnames from 'classnames';

import { PageButtons } from '@codametrix/ui-components';
import {
  AmplifyCore,
  caseUtils,
  commonEnums,
  cmxDateTime
} from '@codametrix/ui-common';
import { StatusDescription } from '../case-status/case-status';
import { Conditional } from '@codametrix/ui-components';
import HelpIcon from '../../assets/help.svg';
import { joinStrings } from '../../utils/utils';
import { MenuButton } from '../menu-button/menu-button';
import { MenuItem } from '../menu-button/menu-item';
import { CaseHold } from '../case-hold/case-hold';
import { CaseExclusion } from '../case-exclusion/case-exclusion';
import { Modal } from '@codametrix/ui-components';
import { CMXIcon } from '@codametrix/ui-components';
import { CaseAlert } from '../case-alert/case-alert';
import pluralize from 'pluralize';
import { CaseComment } from '../case-comment/case-comment';
import { launchAmplifyCase } from '../case-detail/window-open-args';
import './styles/index.scss';
const { conditionalUtils, benchEditable: benchEditableCore } = AmplifyCore;
const { getChildWindowParams } = caseUtils;

const {
  CapabilityAvailabilityReason,
  HoldReasonType,
  DocumentStatus,
  CursorOperation,
  AccelerateProcessStatus,
  CaseAlertType
} = commonEnums;

const DEFAULT_PROPS: Partial<Accelerate.CodeBenchActionBarProps> = {
  processInstanceGuids: [],
  benchEditable: { ...AmplifyCore.state.DEFAULT_BENCH_EDITABLE },
  capabilities: { ...AmplifyCore.state.capabilities },
  caseVersions: []
};

const locatorFn = (activeItem: CMxAPI.CaseRunV2, item: string) => {
  const [, processInstanceGuid] = item.split(':');
  return processInstanceGuid === activeItem.process_instance_guid;
};

const getCaseVersionDate = (version: AmplifyAPI.CaseVersion): Date | string => {
  const dateVersion = new Date(version.processInstanceCreatedDate);
  if (isNaN(dateVersion.valueOf())) {
    return version.processInstanceCreatedDate;
  } else {
    return dateVersion;
  }
};

class CodeBenchActionBar extends React.Component<Accelerate.CodeBenchActionBarProps> {
  getViewOnlyReason = (
    capability: CMxCommonApp.Capability,
    benchEditable: Accelerate.BenchEditableState
  ) => {
    let displayMessage;
    let stringBundle = {};
    if (benchEditable.cancelled) {
      displayMessage = 'This case is cancelled.';
    } else if (benchEditable.submitted) {
      displayMessage = 'This case is submitted.';
    }
    displayMessage =
      (displayMessage ?? '') +
      'If you need permissions to edit this case, see your administrator.';

    if (
      conditionalUtils.capabilityValue(
        this.props.capabilities?.capabilities?.oldCaseLoadedAlert?.on
      )
    ) {
      displayMessage = 'You are viewing a non-codable version of this case';
    }

    stringBundle[CapabilityAvailabilityReason.CONFIGURATION] = displayMessage;
    let reasons = conditionalUtils.getStrings(capability.on, stringBundle);
    return joinStrings(reasons, '.');
  };

  constructor(props: Accelerate.CodeBenchActionBarProps) {
    super(props);
  }

  render() {
    const props: Accelerate.CodeBenchActionBarProps = {
      ...DEFAULT_PROPS,
      ...this.props
    };
    const {
      cases,
      benchEditable,
      processInstanceGuids,
      loadState,
      activeItem,
      processInstance,
      capabilities,
      toggleHoldCaseModal,
      toggleExclusionCaseModal,
      showLateCaseClearModal,
      toggleSplitCaseModal,
      toggleChargeableCase,
      holdCase,
      holdCaseStatus,
      improvements,
      caseVersions,
      toggleSubmitOldCaseModal,
      getCaseVersions,
      cursorAPIContext,
      submitOldCaseModal,
      caseAccessTime
    } = props;

    const hasMulti = cases?.length > 1;
    const hasMultipleProcessInstances = processInstanceGuids?.length > 1;
    const showLoading = loadState.loading || loadState.initial;

    let buttonCancel,
      buttonSubmit,
      processStatusDetail,
      processStatusStyleClass,
      processStatusTitle;

    const statusDisplay = caseUtils.deriveStatus(activeItem);

    function handleAction(buttonAction) {
      let loc = processInstanceGuids.findIndex((item) => {
        return item === buttonAction.item;
      });
      if (processInstanceGuids[loc]) {
        props.handleTraversal(processInstanceGuids[loc]);
      }
    }

    function handleCancel(event: React.MouseEvent) {
      event.preventDefault();
      props.onInvoicesCancel(processInstance);
    }

    function navigateWorklist(
      service_line: string,
      case_uid: string,
      processInstanceGuid: string
    ) {
      props.navigate(
        `/cmx/worklist/${service_line}/case/${case_uid}/case-run/${processInstanceGuid}`
      );
    }

    async function handleSubmit(
      event: React.MouseEvent,
      vwComment: string = null
    ) {
      event.preventDefault();
      // Disable the submit button to prevent multiple clicks during the submission process
      const button = event.currentTarget as HTMLButtonElement;
      button.disabled = true;

      const cursorAPIPayload: CMxCommonApp.CursorAPIPayload = {
        operation: CursorOperation.NEXT,
        cursorAPIContext
      };
      try {
        await getCaseVersions(processInstance);

        if (isCodeableVersion) {
          await props.onInvoicesSubmisson({
            processInstance: processInstance,
            uiMode: props.UIMode,
            vwComment,
            cursorAPIPayload,
            submitWithOverrideButton: false,
            caseAccessTime,
            activeItem: activeItem
          });
        } else {
          toggleSubmitOldCaseModal();
        }
      } finally {
        // Re-enable the submit button after the submission process is complete
        button.disabled = false;
      }
    }

    function handleNextPrev(event: React.MouseEvent, operation: string) {
      event.preventDefault();
      const cursorAPIPayload: CMxCommonApp.CursorAPIPayload = {
        operation,
        cursorAPIContext
      };
      props.onCursorAction(cursorAPIPayload);
    }

    function handleOpenCase(processInstanceGuid: string, caseUid?: string) {
      if (processInstanceGuid) {
        const childWindowParams = getChildWindowParams(
          caseUid ?? activeItem?.case_uid,
          processInstanceGuid,
          activeItem?.service_line,
          launchAmplifyCase,
          activeItem?.case_id
        );
        props.openChildWindow(childWindowParams);
      }
    }

    if (processInstance) {
      processStatusDetail = benchEditableCore.determineProcessStatus(
        {
          isChargeable: processInstance.isChargeable,
          processInstanceStatus: processInstance.status,
          holdReason: holdCaseStatus?.hold_reason,
          holdComment: holdCaseStatus?.comment,
          qaStatus: processInstance.qaStatus,
          roles: this.props?.roles,
          cancelled: processInstance.cancelled,
          benchEditable,
          userContext: this.props?.userContext
        },
        true,
        activeItem?.parent_caserun_uid
      );
      processStatusStyleClass = benchEditableCore.fecthProcessStatusClasses(
        processStatusDetail,
        processInstance?.hold_reason
      );
      processStatusTitle = processStatusDetail?.toLowerCase().includes('hold')
        ? holdCaseStatus?.comment
        : '';
    }
    const styleClasses = [
      'code-bench-action-bar',
      {
        'bench-pre-editable': benchEditable.waiting,
        'bench-loading': loadState.loading,
        'multiple-cases': hasMulti,
        'single-case': cases.length == 0
      }
    ];

    if (benchEditable.editable) {
      buttonCancel = (
        <button
          onClick={handleCancel}
          className="chakra-secondary-button qa-cancel-action-link action-bar-button">
          Void
        </button>
      );
      buttonSubmit = (
        <button
          onClick={handleSubmit}
          className="chakra-primary-button submit-btn qa-submit-action-link action-bar-button">
          Check & Submit
        </button>
      );
    } else if (benchEditable.submitted) {
      buttonSubmit = (
        <button
          disabled
          className="chakra-primary-button submit-btn disabled action-bar-button">
          Checked & Submitted
        </button>
      );
    } else if (benchEditable.cancelled) {
      buttonCancel = (
        <button
          disabled
          className="chakra-secondary-button submit-btn disabled action-bar-button">
          Voided
        </button>
      );
    }

    const pageButtons = !hasMultipleProcessInstances ? (
      false
    ) : (
      <PageButtons
        locatorFn={locatorFn}
        handleAction={handleAction}
        items={processInstanceGuids}
        activeItem={activeItem}
        key="page-buttons"
      />
    );

    let viewOnlyComponent = (
      <div className="view-only-section">
        <Conditional on={!capabilities.submitInProgress}>
          <span>View only Mode </span>
        </Conditional>
        <Conditional on={capabilities.submitInProgress}>
          <span>Submission in progress... </span>
        </Conditional>
        <strong
          title={this.getViewOnlyReason(
            capabilities.capabilities.viewOnlyCase,
            benchEditable
          )}>
          <img className="help-icon-image" src={HelpIcon} />
        </strong>
      </div>
    );

    const statusClasses = [
      'cmx-chip',
      props.className,
      'standalone-status',
      'description',
      statusDisplay.styleClass
    ];

    const versionClasses = [
      processStatusDetail ? 'withProcessStatus' : 'withOutProcessStatus',
      processStatusStyleClass && [
        ...processStatusStyleClass?.filter((item) => item !== 'process-status')
      ]
    ];

    /**
     * 1. improvement with provider query required and
     *  a. with no provider is counted as open improvement.
     *  b. with atleast one open provider reason is counted as open improvement.
     * 2. improvement with provider query
     *  a. not required is not an open improvement.
     *  b. required and had no provider reason as open, is not an open improvement.
     */
    const improvementCount = improvements?.improvements?.filter(
      (improvement) =>
        improvement?.provider_query &&
        (improvement?.providers?.length === 0 ||
          improvement?.providers?.some(
            (provider) =>
              provider?.provider_query_status === DocumentStatus.OPEN
          ))
    ).length;

    const currentVersionPosition =
      caseVersions?.findIndex(
        (caseVersion) =>
          caseVersion.processInstanceGuid === processInstance?.guid?.uuid
      ) ?? -1;

    const currentVersion =
      typeof currentVersionPosition === 'number' &&
      currentVersionPosition !== -1
        ? currentVersionPosition + 1
        : null;

    const newestVersionObject: AmplifyAPI.CaseVersion =
      caseVersions?.length > 0 ? caseVersions.slice().reverse().find(
        (caseVersion) => caseVersion.processInstanceStatus?.indexOf('REPLACED') < 0
      ) : null;

    const currentVersionObject: AmplifyAPI.CaseVersion = caseVersions?.find(
      (caseVersion) =>
        caseVersion.processInstanceGuid === processInstance?.guid?.uuid
    );

    const previousVersions = caseVersions?.filter(
      (_, index) => index < currentVersionPosition
    );

    const previousSentCase = previousVersions?.find(
      (previousVersion) =>
        previousVersion.processInstanceStatus ===
          AccelerateProcessStatus.SUBMITTED ||
        previousVersion.processInstanceStatus ===
          AccelerateProcessStatus.CANCELLED ||
        previousVersion.processInstanceStatus === AccelerateProcessStatus.SENT
    );

    let isCodeableVersion = true;

    if (
      newestVersionObject?.processInstanceGuid &&
      processInstance?.guid?.uuid
    ) {
      isCodeableVersion =
        newestVersionObject?.processInstanceGuid ===
          processInstance?.guid?.uuid &&
        (processInstance.status === commonEnums.AccelerateProcessStatus.NEW ||
          processInstance.status ===
            commonEnums.AccelerateProcessStatus.IN_PROGRESS ||
          (processInstance?.qaStatus === commonEnums.ProcessQAStatus.IN_QA &&
            processInstance.status ===
              commonEnums.AccelerateProcessStatus.SUBMITTED));
    }

    const PrefixTitle = () => {
      if (
        caseVersions.every(
          (caseVersion) =>
            caseVersion?.processInstanceStatus !==
            AccelerateProcessStatus.SUBMITTED
        ) &&
        previousVersions
          .filter(
            (caseVersion) =>
              caseVersion.processInstanceGuid !== processInstance?.guid?.uuid
          )
          .some(
            (caseVersion) =>
              caseVersion?.processInstanceStatus ===
                AccelerateProcessStatus.IN_PROGRESS ||
              caseVersion?.processInstanceStatus === AccelerateProcessStatus.NEW
          ) &&
        (currentVersionObject?.processInstanceStatus ===
          AccelerateProcessStatus.NEW ||
          currentVersionObject?.processInstanceStatus ===
            AccelerateProcessStatus.IN_PROGRESS)
      ) {
        return <div className="dot bg-new"></div>;
      }

      if (capabilities?.capabilities?.previousSentCaseAlert?.on.value) {
        return <div className="dot bg-update"></div>;
      }

      return null;
    };

    return (
      <div className={classnames(styleClasses)}>
        {
          <Conditional every={[toggleSplitCaseModal]}>
            <CaseComment
              title="Are you sure you want to split the case?"
              subtitle="This case will be considered non-chargeable, will be kept for reference, and charges are not submitted for billing."
              submitLabel="yes, split the case"
              mandatoryComment={false}
              actionOnlyNoComment={true}
              onCancel={props.hideSplitCaseModal}
              onSubmit={() => {
                props.splitCase(processInstance);
              }}
            />
          </Conditional>
        }

        {toggleHoldCaseModal && (
          <CaseHold
            title="Reason for holding a case"
            onHold={(holdReason: string, comment: string) =>
              holdCase({
                holdReason,
                comment,
                processInstance: processInstance
              })
            }
            onCancel={props.hideHoldCaseModal}
          />
        )}

        <Conditional {...capabilities.capabilities.toggleCaseComment}>
          <CaseComment
            title="Remove hold"
            submitLabel="Remove Hold"
            mandatoryComment={false}
            onCancel={props.hideCaseCommentModal}
            onSubmit={(comment: string) =>
              holdCase({
                holdReason: HoldReasonType.OFF_HOLD,
                comment,
                coding_manager_id: holdCaseStatus?.coding_manager_id,
                processInstance
              })
            }
          />
        </Conditional>

        {toggleExclusionCaseModal && (
          <CaseExclusion
            title={`Are you sure you want to ${
              processInstance?.isChargeable ? 'exclude' : 'include'
            } this case?`}
            isChargeable={processInstance?.isChargeable}
            onCaseExclusion={() =>
              toggleChargeableCase({
                serviceLine: processInstance.serviceLine,
                processInstanceGuid: processInstance.guid.uuid,
                isChargeable: !processInstance?.isChargeable
              })
            }
            onCancel={props.hideExclusionCaseModal}
          />
        )}

        {showLateCaseClearModal && (
          <CaseComment
            title="Are you sure you want to clear this late update notification. It cannot be undone?"
            submitLabel="yes, clear the notiifcation"
            mandatoryComment={false}
            actionOnlyNoComment={true}
            onCancel={() => props.toggleLateCaseUpdateClearModal(false)}
            onSubmit={() => {
              props.clearLateCaseUpdateNotification(processInstance);
            }}
          />
        )}

        {submitOldCaseModal && (
          <Modal
            title="More recent version found"
            onClose={toggleSubmitOldCaseModal}>
            <p className="submit-stale-case-warning">
              This version is no longer the most recent codable version of this case. It
              is locked from future editing. Please make your edits on the new
              version.
            </p>

            <div className="case-exclusion-buttons-group">
              <button
                onClick={toggleSubmitOldCaseModal}
                className="chakra-secondary-button">
                Cancel, keep me here
              </button>
              <button
                onClick={() =>
                  handleOpenCase(newestVersionObject?.processInstanceGuid)
                }
                className={classnames('chakra-primary-button')}>
                Ok, take me to the new case
              </button>
            </div>
          </Modal>
        )}

        {!loadState.loading && (
          <div className="action-bar coding-view-v1">
            <div className="nav-bar-columns">
              <div className="nav-bar-column nav-bar-left-column align-left">
                <div className="cols">
                  <div className="col">
                    <div className={classnames(statusClasses)}>
                      <StatusDescription
                        caseRun={activeItem}
                        isPreGoLive={props.isPreGoLive}
                        secondaryIndicatorVisibility={false}
                      />
                    </div>
                  </div>
                  <Conditional every={[!!activeItem?.parent_case]}>
                    <div className="col">
                      <MenuButton
                        label="Merged"
                        className="merged-case-menu-button">
                        {activeItem?.child_case_list?.map(
                          (childCase, index) => (
                            <MenuItem
                              key={index}
                              onSelect={() =>
                                handleOpenCase(
                                  childCase.caserun_uid,
                                  childCase.case_uid
                                )
                              }>
                              <div className="child-case-menu-item">
                                <span>{`Case #${childCase.case_id}`}</span>
                                <div className="menu-item-tags">
                                  <Conditional
                                    every={[childCase.type === 'primary']}>
                                    <span className="merge-originating-case primary">
                                      Primary
                                    </span>
                                  </Conditional>
                                  <Conditional
                                    every={[
                                      processInstance?.status !==
                                        AccelerateProcessStatus.REMOVED
                                    ]}>
                                    <span className="merge-originating-case inactive">
                                      Inactive
                                    </span>
                                  </Conditional>
                                </div>
                              </div>
                            </MenuItem>
                          )
                        )}
                      </MenuButton>
                    </div>
                  </Conditional>
                  <div className="col">
                    <div className="version-process">
                      {caseVersions?.length > 0 && (
                        <MenuButton
                          label={
                            currentVersion ? `Version ${currentVersion}` : ''
                          }
                          disabled={caseVersions.length === 1}
                          prefixTitle={<PrefixTitle />}
                          className={classnames(versionClasses)}>
                          {caseVersions.map((caseVersion, caseVersionIndex) => (
                            <MenuItem
                              key={caseVersionIndex}
                              onSelect={() =>
                                handleOpenCase(caseVersion.processInstanceGuid)
                              }>
                              <div className="version-item">
                                <p>Version {caseVersionIndex + 1}</p>
                                {caseVersion.processInstanceCreatedDate && (
                                  <p>
                                    Filing date:{' '}
                                    {cmxDateTime.format(
                                      getCaseVersionDate(caseVersion),
                                      cmxDateTime.FORMATS.DATE_TIME_UI
                                    )}
                                  </p>
                                )}
                                {caseVersion.processInstanceGuid !==
                                  processInstance?.guid?.uuid &&
                                  (caseVersion.processInstanceStatus ===
                                    AccelerateProcessStatus.IN_PROGRESS ||
                                    caseVersion.processInstanceStatus ===
                                      AccelerateProcessStatus.NEW) && (
                                    <p>Coding Started</p>
                                  )}
                              </div>
                            </MenuItem>
                          ))}
                        </MenuButton>
                      )}
                      {processStatusDetail && (
                        <div
                          className={`${classnames(
                            processStatusStyleClass
                          )} process-col`}
                          title={processStatusTitle}>
                          {processStatusDetail}
                        </div>
                      )}
                    </div>
                  </div>
                  {improvementCount !== 0 && (
                    <div className="col">
                      <div
                        className="cmx-chip improvement-reasons-needed"
                        title={`${improvementCount} improvement ${pluralize(
                          'reason',
                          improvementCount
                        )} need to be resolved`}>
                        <CMXIcon
                          svgIconName="BiomeReports"
                          svgIconSize="12"
                          svgTitleText={`${improvementCount} improvement ${pluralize(
                            'reason',
                            improvementCount
                          )} need to be resolved`}
                        />
                        {improvementCount}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="nav-bar-column nav-bar-right-column charge-submission-column action-column action-bar-right-column">
                <div className="cols right-cols">
                  <Conditional {...capabilities.capabilities.pagination}>
                    <div className="col">{pageButtons}</div>
                  </Conditional>
                  {!showLoading && (
                    <Conditional
                      {...capabilities.capabilities.servicedeskAction}>
                      <div className="col">
                        <button
                          onClick={(ev) =>
                            handleNextPrev(ev, CursorOperation.NEXT)
                          }
                          className="chakra-secondary-button prev-next-button action-bar-button">
                          Next
                          <CMXIcon
                            svgIconName="BiomeRightArrow"
                            svgIconSize="12"
                          />
                        </button>
                      </div>
                    </Conditional>
                  )}

                  <Conditional {...capabilities.capabilities.actionMenu}>
                    <MenuButton label="Actions">
                      <Conditional
                        {...capabilities.capabilities.actionMenuCancelButton}
                        displayMode="flex">
                        <MenuItem onSelect={handleCancel}>Void Case</MenuItem>
                      </Conditional>

                      <Conditional {...capabilities.capabilities.holdCase}>
                        <MenuItem onSelect={props.showHoldCaseModal}>
                          Hold Case
                        </MenuItem>
                      </Conditional>
                      <Conditional
                        {...capabilities.capabilities.removeHoldCase}>
                        <MenuItem onSelect={props.showCaseCommentModal}>
                          Remove Hold
                        </MenuItem>
                      </Conditional>
                      <Conditional {...capabilities.capabilities.createCase}>
                        <MenuItem
                          onSelect={() => props.navigateCreateCase(activeItem)}>
                          Create Case from Encounter/HAR
                        </MenuItem>
                      </Conditional>
                      <Conditional
                        {...capabilities.capabilities.toggleChargeableCase}>
                        <MenuItem onSelect={props.showExclusionCaseModal}>
                          {processInstance?.isChargeable
                            ? 'Exclude Case'
                            : 'Include Case'}
                        </MenuItem>
                      </Conditional>
                      <Conditional
                        {...capabilities.capabilities.mergeCapability}>
                        <MenuItem
                          onSelect={() =>
                            props.showMergeCasesModal({
                              caseRun: activeItem,
                              cursorAPIContext
                            })
                          }>
                          Merge Case
                        </MenuItem>
                      </Conditional>
                      <Conditional
                        {...capabilities.capabilities.splitCapability}>
                        <MenuItem onSelect={() => props.showSplitCaseModal()}>
                          Split Case
                        </MenuItem>
                      </Conditional>
                      <Conditional
                        {...capabilities.capabilities
                          .lateCaseClearNotification}>
                        <MenuItem
                          onSelect={() => props.toggleLateCaseClearModal(true)}>
                          Clear late update notification
                        </MenuItem>
                      </Conditional>
                    </MenuButton>
                  </Conditional>

                  <Conditional
                    {...capabilities.capabilities.generalCancelButton}>
                    <div className="col">{buttonCancel}</div>
                  </Conditional>

                  <Conditional {...capabilities.capabilities.submitCase}>
                    <div className="col">{buttonSubmit}</div>
                  </Conditional>
                  <Conditional {...capabilities.capabilities.viewOnlyCase}>
                    <div className="col">{viewOnlyComponent}</div>
                  </Conditional>
                </div>
              </div>
            </div>
            <Conditional {...capabilities.capabilities.previousSentCaseAlert}>
              <CaseAlert alertType={CaseAlertType.ERROR}>
                This is an update to a{' '}
                <a
                  className="anchor-chakra-link"
                  onClick={() =>
                    handleOpenCase(previousSentCase?.processInstanceGuid)
                  }>
                  previously submitted case
                </a>
                .
              </CaseAlert>
            </Conditional>
            <Conditional {...capabilities.capabilities.oldCaseLoadedAlert}>
              <CaseAlert alertType={CaseAlertType.WARNING}>
                You are viewing a non-codable version of this case. View the{' '}
                <a
                  className="anchor-chakra-link"
                  onClick={() =>
                    handleOpenCase(newestVersionObject?.processInstanceGuid)
                  }>
                  codable version
                </a>
              </CaseAlert>
            </Conditional>
            <Conditional some={[!!activeItem?.parent_process_instance_uid]}>
              <CaseAlert alertType={CaseAlertType.INFO} alertSize="sm">
                <Conditional
                  every={[!!activeItem?.parent_process_instance_uid]}>
                  <span>
                    This case is no longer active. It has been merged into: case
                    <a
                      className="anchor-chakra-link"
                      onClick={() =>
                        handleOpenCase(
                          activeItem?.parent_process_instance_uid,
                          activeItem?.parent_case_uid
                        )
                      }>
                      {' '}
                      #{activeItem?.parent_case_id}
                    </a>
                  </span>
                </Conditional>
              </CaseAlert>
            </Conditional>
          </div>
        )}
      </div>
    );
  }
}

export { CodeBenchActionBar };
